import { motion } from 'framer-motion';
import { Lightbulb, Shield, Sparkles, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const team = [
  { name: 'Alex Chen', role: 'CEO & Co-Founder', img: 'https://images.unsplash.com/photo-1769636929261-e913ed023c83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY2ODAyNDV8MA&ixlib=rb-4.1.0&q=85&w=200&h=200' },
  { name: 'Sarah Park', role: 'CTO', img: 'https://images.unsplash.com/photo-1771898343647-bd979ad8cca5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY2ODAyNDV8MA&ixlib=rb-4.1.0&q=85&w=200&h=200' },
  { name: 'Marcus Rivera', role: 'Head of Product', img: 'https://images.pexels.com/photos/4687549/pexels-photo-4687549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200' },
  { name: 'Priya Sharma', role: 'Head of Engineering', img: 'https://images.unsplash.com/photo-1758599543120-4e462429a4d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMGNvcnBvcmF0ZSUyMGhlYWRzaG90fGVufDB8fHx8MTc3NjY4MTI1N3ww&ixlib=rb-4.1.0&q=85&w=200&h=200' },
];

const values = [
  { icon: Sparkles, title: 'Innovation', desc: 'We push boundaries and embrace new ideas to solve the hardest data challenges.' },
  { icon: Shield, title: 'Trust', desc: 'Enterprise-grade security and governance are built into everything we create.' },
  { icon: Lightbulb, title: 'Simplicity', desc: 'Complex technology should feel effortless. We obsess over reducing friction.' },
  { icon: Users, title: 'Governance', desc: 'Human oversight and accountability are core to every AI-powered decision.' },
];

export default function About() {
  return (
    <div className="bg-[#0a0a1a] min-h-screen">
      {/* Hero */}
      <section data-testid="about-hero" className="relative py-24 md:py-32">
        <div className="glow-spot w-[400px] h-[400px] bg-[#3b82f6] top-[-80px] left-[50%] opacity-10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">About Us</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Building the future of<br /><span className="gradient-text">data intelligence</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 max-w-2xl mx-auto">
              We're on a mission to democratize data access for every team, in every organization, regardless of technical skill.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section data-testid="about-story" className="py-24 md:py-32 bg-[#111122]/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Our Story</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-8" style={{ fontFamily: 'Outfit' }}>
              Why we built DQP
            </motion.h2>
            <motion.div variants={fadeUp} className="space-y-6 text-slate-400 leading-relaxed">
              <p>
                We spent years watching enterprise data teams drown in tool sprawl. Fifteen different tools to connect, catalog, query, visualize, and govern data. Each tool with its own contracts, learning curves, and integration headaches.
              </p>
              <p>
                The $47 billion data infrastructure market was growing, but so was the frustration. Data engineers spent 80% of their time on plumbing instead of insight. Business users waited days for answers to simple questions.
              </p>
              <p>
                We built what the industry needed: a single, AI-native platform that replaces the fragmented stack with an intelligent workbench. One place to connect all your data, discover what you have, and query it with natural language — with governance built in from day one.
              </p>
              <p className="text-white font-medium">
                DQP is the result — and we're just getting started.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="about-team" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">The Team</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
              Meet the people behind DQP
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`team-member-${i}`} className="glass-card p-6 text-center group">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-2 border-white/10 group-hover:border-[#00d4ff]/30 transition-colors">
                  <img src={member.img} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-white font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>{member.name}</h3>
                <p className="text-slate-500 text-sm mt-1">{member.role}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="py-24 md:py-32 bg-[#111122]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Our Values</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
              What drives us
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#00d4ff]/10 flex items-center justify-center mx-auto mb-4">
                  <v.icon className="w-6 h-6 text-[#00d4ff]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit' }}>{v.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto px-6">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Join the mission
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 mb-8">We're building the future of data intelligence. Come build it with us.</motion.p>
          <motion.div variants={fadeUp} className="flex justify-center gap-4">
            <Link to="/contact">
              <Button data-testid="about-cta-btn" className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)]">
                Get in Touch <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
